import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PieChart } from "@mui/x-charts";
import type { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router";
import { DataGrid } from "../../../components/data-grid";
import { theme } from "../../../theme/theme";
import type { FlakinessMetrics, FlakyTestInfo } from "../../../types";

interface FlakinessTabProps {
  data?: FlakinessMetrics;
  isLoading?: boolean;
}

const pfsThresholds = {
  reliable: { max: 0.05, color: theme.palette.success.main, label: "Reliable" },
  moderate: {
    max: 0.15,
    color: theme.palette.warning.light,
    label: "Moderate",
  },
  concerning: {
    max: 0.3,
    color: theme.palette.warning.main,
    label: "Concerning",
  },
  highly_flaky: {
    max: 1,
    color: theme.palette.error.main,
    label: "Highly Flaky",
  },
};

function getPfsCategory(score: number): keyof typeof pfsThresholds {
  if (score < 0.05) return "reliable";
  if (score < 0.15) return "moderate";
  if (score < 0.3) return "concerning";
  return "highly_flaky";
}

function PfsScoreChip({ score }: { score: number }) {
  const category = getPfsCategory(score);
  const config = pfsThresholds[category];
  return (
    <Chip
      label={`${(score * 100).toFixed(1)}%`}
      size="small"
      sx={{
        backgroundColor: alpha(config.color, 0.15),
        color: config.color,
        fontWeight: 600,
      }}
    />
  );
}

const columns: GridColDef<FlakyTestInfo>[] = [
  {
    field: "test_name",
    headerName: "Test Name",
    flex: 2,
    minWidth: 200,
    renderCell: (params) => (
      <Link
        to={`/tests/${params.row.test_id}`}
        style={{ color: theme.palette.primary.main, textDecoration: "none" }}
      >
        {params.value}
      </Link>
    ),
  },
  {
    field: "pfs_score",
    headerName: "PFS Score",
    width: 120,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => <PfsScoreChip score={params.value} />,
    description: "Probabilistic Flakiness Score (0-100%)",
  },
  {
    field: "confidence",
    headerName: "Confidence",
    width: 150,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const lower = params.row.pfs_confidence_lower;
      const upper = params.row.pfs_confidence_upper;
      if (lower == null || upper == null) return "-";
      return (
        <Tooltip
          title={`95% confidence interval: ${(lower * 100).toFixed(1)}% - ${(upper * 100).toFixed(1)}%`}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {(lower * 100).toFixed(0)}% - {(upper * 100).toFixed(0)}%
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "pfs_num_observations",
    headerName: "Observations",
    width: 120,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => params.value ?? "-",
  },
  {
    field: "last_flaky_run_at",
    headerName: "Last Flaky Run",
    width: 150,
    valueFormatter: (value) => (value ? new Date(value).toLocaleDateString() : "Never"),
  },
];

function OverallPfsGauge({ score, isLoading }: { score: number; isLoading?: boolean }) {
  const category = getPfsCategory(score);
  const config = pfsThresholds[category];

  if (isLoading) {
    return <Skeleton variant="rectangular" height={120} />;
  }

  return (
    <Box sx={{ textAlign: "center", py: 2 }}>
      <Typography variant="h2" sx={{ color: config.color, fontWeight: 700 }}>
        {(score * 100).toFixed(1)}%
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Average Flakiness Score
      </Typography>
      <Chip
        icon={
          category === "reliable" ? (
            <CheckCircleIcon />
          ) : category === "highly_flaky" ? (
            <ErrorIcon />
          ) : (
            <WarningIcon />
          )
        }
        label={config.label}
        sx={{
          backgroundColor: alpha(config.color, 0.15),
          color: config.color,
          fontWeight: 600,
        }}
      />
    </Box>
  );
}

export function FlakinessTab({ data, isLoading }: FlakinessTabProps) {
  const pfsDistribution = data?.pfs_distribution ?? {};
  const topFlakyTests = data?.top_flaky_tests ?? [];

  const distributionData = Object.entries(pfsDistribution)
    .filter(([, count]) => count > 0)
    .map(([category, count], index) => ({
      id: index,
      value: count,
      label: pfsThresholds[category as keyof typeof pfsThresholds]?.label || category,
      color:
        pfsThresholds[category as keyof typeof pfsThresholds]?.color || theme.palette.grey[500],
    }));

  return (
    <Stack spacing={3}>
      {/* PFS Description */}
      <Card sx={{ backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <InfoIcon color="info" fontSize="small" sx={{ mt: 0.3 }} />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                What is PFS (Probabilistic Flakiness Score)?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PFS is a statistical measure that estimates the probability of a test producing
                inconsistent results (flaky behavior). It uses Bayesian inference to calculate the
                likelihood that a test will fail intermittently, based on historical pass/fail
                patterns. Unlike simple failure rates, PFS accounts for statistical uncertainty and
                provides confidence intervals, making it more reliable for identifying genuinely
                flaky tests vs. those with occasional legitimate failures.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Time Filter Notice */}
      <Alert severity="info" icon={<ScheduleIcon fontSize="small" />} sx={{ py: 0.5 }}>
        <Typography variant="body2">
          Flakiness scores are calculated from all historical test data and are not affected by the
          time range filter.
        </Typography>
      </Alert>

      {/* Overview Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: 200 }}>
            <CardContent>
              <OverallPfsGauge score={data?.avg_pfs_score ?? 0} isLoading={isLoading} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: 200 }}>
            <CardContent sx={{ height: "100%" }}>
              <Stack
                spacing={2}
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                {isLoading ? (
                  <Skeleton variant="rectangular" height={60} width={80} />
                ) : (
                  <Typography variant="h2" color="warning.main" sx={{ fontWeight: 700 }}>
                    {data?.flaky_tests_count ?? 0}
                  </Typography>
                )}
                <Typography variant="subtitle1" color="text.secondary">
                  Flaky Tests (PFS {">"} 15%)
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: 200 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                PFS Thresholds Guide
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {Object.entries(pfsThresholds).map(([key, config]) => (
                  <Stack key={key} direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: config.color,
                      }}
                    />
                    <Typography variant="body2">
                      <strong>{config.label}</strong>:{" "}
                      {key === "reliable"
                        ? "< 5%"
                        : key === "moderate"
                          ? "5-15%"
                          : key === "concerning"
                            ? "15-30%"
                            : "> 30%"}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Distribution Chart */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <InfoIcon color="info" fontSize="small" />
                <Typography variant="h6">Test Distribution by PFS</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Number of tests in each flakiness category
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={280} sx={{ mt: 2 }} />
              ) : distributionData.length > 0 ? (
                <PieChart
                  series={[{ data: distributionData, innerRadius: 50 }]}
                  height={280}
                  margin={{ top: 20, bottom: 20, left: 0, right: 0 }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 280,
                  }}
                >
                  <Typography color="text.secondary">No PFS data available yet</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ height: 400 }}>
            <CardContent sx={{ height: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <WarningIcon color="warning" fontSize="small" />
                <Typography variant="h6">Top 10 Flaky Tests</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Tests with the highest flakiness scores
              </Typography>
              <Box sx={{ height: 280 }}>
                <DataGrid
                  rows={topFlakyTests.map((t, idx) => ({
                    ...t,
                    id: t.test_id || idx,
                  }))}
                  columns={columns}
                  loading={isLoading}
                  hideFooter
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Test Suites Flakiness Section */}
      <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
        Test Suite Flakiness
      </Typography>

      {/* Suite Overview Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ height: 220 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                justifyContent="center"
                mb={1}
              >
                <Typography variant="subtitle2">Suite Run PFS</Typography>
                <Tooltip title="Flakiness of the entire suite based on historical suite run outcomes (pass/fail patterns)">
                  <InfoIcon fontSize="small" color="info" sx={{ fontSize: 16 }} />
                </Tooltip>
              </Stack>
              <OverallPfsGauge score={data?.avg_suite_pfs_score ?? 0} isLoading={isLoading} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ height: 220 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                justifyContent="center"
                mb={1}
              >
                <Typography variant="subtitle2">Individual Tests PFS</Typography>
                <Tooltip title="Average flakiness of individual tests within suites (measures test-level reliability)">
                  <InfoIcon fontSize="small" color="info" sx={{ fontSize: 16 }} />
                </Tooltip>
              </Stack>
              <OverallPfsGauge score={data?.avg_suite_tests_pfs_score ?? 0} isLoading={isLoading} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ height: 220 }}>
            <CardContent sx={{ height: "100%" }}>
              <Stack
                spacing={2}
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                {isLoading ? (
                  <Skeleton variant="rectangular" height={60} width={80} />
                ) : (
                  <Typography variant="h2" color="warning.main" sx={{ fontWeight: 700 }}>
                    {data?.flaky_suites_count ?? 0}
                  </Typography>
                )}
                <Typography variant="subtitle1" color="text.secondary">
                  Flaky Suites (PFS {">"} 15%)
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ height: 220 }}>
            <CardContent>
              <Stack direction="row" spacing={0.5} alignItems="center" mb={1}>
                <Typography variant="subtitle2">What's the difference?</Typography>
                <InfoIcon fontSize="small" color="info" sx={{ fontSize: 16 }} />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                <strong>Suite Run PFS:</strong> Does the suite as a whole pass/fail inconsistently?
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Individual Tests PFS:</strong> How flaky are the tests inside the suite?
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Suite Distribution & Top Flaky Suites */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <InfoIcon color="info" fontSize="small" />
                <Typography variant="h6">Suite Distribution by PFS</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Number of suites in each flakiness category
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={280} sx={{ mt: 2 }} />
              ) : data?.suite_pfs_distribution ? (
                <PieChart
                  series={[
                    {
                      data: Object.entries(data.suite_pfs_distribution).map(([key, value]) => ({
                        id: key,
                        value,
                        label: pfsThresholds[key as keyof typeof pfsThresholds]?.label || key,
                        color:
                          pfsThresholds[key as keyof typeof pfsThresholds]?.color ||
                          theme.palette.grey[500],
                      })),
                      innerRadius: 50,
                    },
                  ]}
                  height={280}
                  margin={{ top: 20, bottom: 20, left: 0, right: 0 }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 280,
                  }}
                >
                  <Typography color="text.secondary">No suite PFS data available yet</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ height: 400 }}>
            <CardContent sx={{ height: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <WarningIcon color="warning" fontSize="small" />
                <Typography variant="h6">Top 10 Flaky Suites</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Test suites with the highest flakiness scores
              </Typography>
              <Box sx={{ height: 280 }}>
                <DataGrid
                  rows={(data?.top_flaky_suites ?? []).map((s, idx) => ({
                    ...s,
                    id: s.suite_id || idx,
                  }))}
                  columns={[
                    {
                      field: "suite_name",
                      headerName: "Suite Name",
                      flex: 2,
                      minWidth: 200,
                      renderCell: (params) => (
                        <Link
                          to={`/tests-main/test-suites/${params.row.suite_id}`}
                          style={{
                            color: theme.palette.primary.main,
                            textDecoration: "none",
                          }}
                        >
                          {params.value}
                        </Link>
                      ),
                    },
                    {
                      field: "pfs_score",
                      headerName: "Suite PFS",
                      width: 100,
                      align: "center",
                      headerAlign: "center",
                      renderCell: (params) => <PfsScoreChip score={params.value} />,
                      description: "Suite flakiness based on suite runs",
                    },
                    {
                      field: "tests_pfs_score",
                      headerName: "Tests PFS",
                      width: 100,
                      align: "center",
                      headerAlign: "center",
                      renderCell: (params) => {
                        if (params.value == null) return "-";
                        return <PfsScoreChip score={params.value} />;
                      },
                      description: "Average test PFS in suite",
                    },
                  ]}
                  loading={isLoading}
                  hideFooter
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
